/**
 * FormSuccess Component
 *
 * A reusable success/confirmation component that provides
 * visual feedback after successful form submissions.
 */

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Zoom,
  Fade,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Celebration as CelebrationIcon,
  ArrowForward as NextIcon,
  Refresh as ResetIcon,
} from '@mui/icons-material';
import { ReactNode, useEffect, useState } from 'react';

export interface FormSuccessProps {
  /**
   * Whether to show the success state
   */
  show: boolean;

  /**
   * Success message title
   */
  title?: string;

  /**
   * Detailed success message
   */
  message?: string;

  /**
   * Success variant affects styling and icon
   */
  variant?: 'success' | 'celebration' | 'info';

  /**
   * Primary action button
   */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };

  /**
   * Secondary action button
   */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };

  /**
   * Auto-hide the success message after a delay (in ms)
   */
  autoHide?: number;

  /**
   * Callback when auto-hide occurs
   */
  onAutoHide?: () => void;

  /**
   * Custom content to render instead of default message
   */
  children?: ReactNode;

  /**
   * Visual style variant
   */
  appearance?: 'alert' | 'card' | 'inline';

  /**
   * Animation variant
   */
  animation?: 'fade' | 'zoom' | 'slide' | 'none';

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Whether to show confetti or celebration effects
   */
  celebrate?: boolean;
}

const FormSuccess = ({
  show,
  title = 'Success!',
  message,
  variant = 'success',
  primaryAction,
  secondaryAction,
  autoHide,
  onAutoHide,
  children,
  appearance = 'alert',
  animation = 'fade',
  className,
  celebrate = false,
}: FormSuccessProps) => {
  const [visible, setVisible] = useState(show);

  // Handle auto-hide
  useEffect((): void | (() => void) => {
    if (show && autoHide) {
      const timer = setTimeout(() => {
        setVisible(false);
        onAutoHide?.();
      }, autoHide);

      return () => clearTimeout(timer);
    } else {
      setVisible(show);
      return;
    }
  }, [show, autoHide, onAutoHide]);

  // Icon based on variant
  const getIcon = () => {
    switch (variant) {
      case 'celebration':
        return <CelebrationIcon fontSize="large" />;
      case 'info':
        return <SuccessIcon fontSize="large" color="info" />;
      default:
        return <SuccessIcon fontSize="large" color="success" />;
    }
  };

  // Color scheme based on variant
  const getColor = () => {
    switch (variant) {
      case 'info':
        return 'info';
      case 'celebration':
        return 'success';
      default:
        return 'success';
    }
  };

  // Action buttons
  const actionButtons = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
      {primaryAction && (
        <Button
          variant="contained"
          onClick={primaryAction.onClick}
          startIcon={primaryAction.icon}
          size="medium"
        >
          {primaryAction.label}
        </Button>
      )}
      {secondaryAction && (
        <Button
          variant="outlined"
          onClick={secondaryAction.onClick}
          startIcon={secondaryAction.icon}
          size="medium"
        >
          {secondaryAction.label}
        </Button>
      )}
    </Stack>
  );

  // Content based on appearance
  const getContent = () => {
    if (children) {
      return children;
    }

    if (appearance === 'card') {
      return (
        <Card
          className={className}
          sx={{
            textAlign: 'center',
            p: 2,
            border: '2px solid',
            borderColor: `${getColor()}.light`,
            bgcolor: `${getColor()}.50`,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2 }}>{getIcon()}</Box>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              color={`${getColor()}.main`}
            >
              {title}
            </Typography>
            {message && (
              <Typography variant="body1" color="text.secondary" paragraph>
                {message}
              </Typography>
            )}
            {(primaryAction || secondaryAction) && actionButtons}
          </CardContent>
        </Card>
      );
    }

    if (appearance === 'inline') {
      return (
        <Box className={className} sx={{ textAlign: 'center', py: 2 }}>
          <Stack spacing={2} alignItems="center">
            {getIcon()}
            <Box>
              <Typography variant="h6" color={`${getColor()}.main`} gutterBottom>
                {title}
              </Typography>
              {message && (
                <Typography variant="body2" color="text.secondary">
                  {message}
                </Typography>
              )}
            </Box>
            {(primaryAction || secondaryAction) && actionButtons}
          </Stack>
        </Box>
      );
    }

    // Default: alert appearance
    return (
      <Alert
        severity={getColor() as any}
        className={className}
        icon={getIcon()}
        action={
          (primaryAction || secondaryAction) && (
            <Stack direction="row" spacing={1}>
              {secondaryAction && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={secondaryAction.onClick}
                  startIcon={secondaryAction.icon}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {primaryAction && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={primaryAction.onClick}
                  startIcon={primaryAction.icon}
                >
                  {primaryAction.label}
                </Button>
              )}
            </Stack>
          )
        }
      >
        <AlertTitle>{title}</AlertTitle>
        {message && <Typography variant="body2">{message}</Typography>}
      </Alert>
    );
  };

  // Animation wrapper
  const AnimatedContent = () => {
    const content = getContent();

    switch (animation) {
      case 'zoom':
        return (
          <Zoom in={visible} timeout={300}>
            <Box>{content}</Box>
          </Zoom>
        );
      case 'fade':
        return (
          <Fade in={visible} timeout={300}>
            <Box>{content}</Box>
          </Fade>
        );
      case 'none':
        return visible ? content : null;
      default:
        return (
          <Fade in={visible} timeout={300}>
            <Box>{content}</Box>
          </Fade>
        );
    }
  };

  if (!visible) {
    return null;
  }

  return <AnimatedContent />;
};

// Preset configurations for common success scenarios
export const FormSuccessPresets = {
  groupCreated: (
    onContinue: () => void,
    onCreateAnother: () => void,
  ): FormSuccessProps => ({
    show: true,
    title: 'Group Created!',
    message: 'Your new group has been successfully created and is ready for members.',
    variant: 'celebration',
    appearance: 'card',
    primaryAction: {
      label: 'View Group',
      onClick: onContinue,
      icon: <NextIcon />,
    },
    secondaryAction: {
      label: 'Create Another',
      onClick: onCreateAnother,
      icon: <ResetIcon />,
    },
    celebrate: true,
  }),

  meetingScheduled: (
    onContinue: () => void,
    onCreateAnother: () => void,
  ): FormSuccessProps => ({
    show: true,
    title: 'Event Scheduled!',
    message: 'Your event has been created and members can now RSVP.',
    variant: 'celebration',
    appearance: 'card',
    primaryAction: {
      label: 'View Event',
      onClick: onContinue,
      icon: <NextIcon />,
    },
    secondaryAction: {
      label: 'Schedule Another',
      onClick: onCreateAnother,
      icon: <ResetIcon />,
    },
    celebrate: true,
  }),

  broadcastSent: (onContinue: () => void): FormSuccessProps => ({
    show: true,
    title: 'Message Sent!',
    message:
      'Your broadcast has been sent successfully. Members will receive push notifications if subscribed.',
    variant: 'success',
    appearance: 'alert',
    primaryAction: {
      label: 'Send Another',
      onClick: onContinue,
      icon: <ResetIcon />,
    },
    autoHide: 5000,
  }),

  profileUpdated: (): FormSuccessProps => ({
    show: true,
    title: 'Profile Updated',
    message: 'Your changes have been saved successfully.',
    variant: 'success',
    appearance: 'alert',
    autoHide: 3000,
  }),

  settingsSaved: (): FormSuccessProps => ({
    show: true,
    title: 'Settings Saved',
    message: 'Your preferences have been updated.',
    variant: 'info',
    appearance: 'alert',
    autoHide: 3000,
  }),
};

export default FormSuccess;
